import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Subject } from 'rxjs';
import { QuestionnaireType } from 'src/API';
import { QuestionnaireRequest } from 'src/app/core/api/questionnaire-request.service';
import { dewsIIIMedicalHistoryAutopopulateConfig } from 'src/app/encounters/encounter-linking/dews-iii-medical-history-linking';
import { AutopopulateService } from 'src/app/shared/autopopulate/autopopulate.service';
import { DryEyeFormGroup } from '../../dry-eye-form/dry-eye-form.model';
import { DEWS_MEDICAL_HISTORY_FIELDS, DewsMedicalHistoryField } from './dews-iii-medical-history.schema';

export { DEWS_MEDICAL_HISTORY_KEYS } from './dews-iii-medical-history.schema';
export const DEWS_DIFFERENTIAL_DIAGNOSIS_FORM_KEY = 'dewsIIIDifferentialDiagnosisScreening';

@Component({
  selector: 'csi-dews-iii-medical-history',
  templateUrl: 'dews-iii-medical-history.component.html',
  styleUrls: ['dews-iii-medical-history.component.scss']
})
export class DewsIIIMedicalHistoryComponent implements OnInit, OnDestroy {
  @Input() formGroup: DryEyeFormGroup;
  @Input() isLocked: boolean;
  @Input() onQuestionnaireLinked$: Subject<QuestionnaireRequest[]>;

  differentialDiagnosisScreeningFormGroup: FormGroup;

  urgentFlag = false;
  painFlag = false;
  systemicFlag = false;
  lateralityFlag = false;
  allergyInfectionFlag = false;

  constructor(private autopopulateService: AutopopulateService) {}

  ngOnInit() {
    const allKeys = DEWS_MEDICAL_HISTORY_FIELDS.map(f => f.key);
    const existingControl = this.formGroup.controls[DEWS_DIFFERENTIAL_DIAGNOSIS_FORM_KEY];

    if (existingControl instanceof FormGroup) {
      this.differentialDiagnosisScreeningFormGroup = existingControl;
    } else {
      const controls: { [key: string]: FormControl } = {};
      allKeys.forEach(key => (controls[key] = new FormControl(null)));
      this.differentialDiagnosisScreeningFormGroup = new FormGroup(controls);

      // DryEyeFormGroup.patchValue may have created a FormControl (not FormGroup) for this
      // key when loading saved data before this component initialized. Extract and restore.
      if (existingControl) {
        const savedValues = existingControl.value;
        if (savedValues && typeof savedValues === 'object') {
          this.differentialDiagnosisScreeningFormGroup.patchValue(savedValues);
        }
        this.formGroup.removeControl(DEWS_DIFFERENTIAL_DIAGNOSIS_FORM_KEY);
      }

      this.formGroup.addControl(DEWS_DIFFERENTIAL_DIAGNOSIS_FORM_KEY, this.differentialDiagnosisScreeningFormGroup);
    }

    this.updateFlags();
    this.differentialDiagnosisScreeningFormGroup.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.updateFlags());

    if (this.onQuestionnaireLinked$) {
      this.onQuestionnaireLinked$
        .pipe(untilDestroyed(this))
        .subscribe((linkedQuestionnaires: QuestionnaireRequest[]) => {
          this.populateFromLinkedQuestionnaires(linkedQuestionnaires);
        });
    }
  }

  ngOnDestroy() {}

  private populateFromLinkedQuestionnaires(linkedQuestionnaires: QuestionnaireRequest[]) {
    if (!linkedQuestionnaires) {
      return;
    }

    let latestDewsQuestionnaire: QuestionnaireRequest = null;

    linkedQuestionnaires.forEach((questionnaire: QuestionnaireRequest) => {
      if (questionnaire.type === QuestionnaireType.DEWSIII_Q) {
        if (latestDewsQuestionnaire) {
          if (
            new Date(latestDewsQuestionnaire.completedAt).getTime() <
            new Date(questionnaire.completedAt).getTime()
          ) {
            latestDewsQuestionnaire = questionnaire;
          }
        } else {
          latestDewsQuestionnaire = questionnaire;
        }
      }
    });

    if (!latestDewsQuestionnaire) {
      return;
    }

    const answers = JSON.parse(latestDewsQuestionnaire.response.answers);

    this.autopopulateService.populateFormGroup(
      this.differentialDiagnosisScreeningFormGroup,
      dewsIIIMedicalHistoryAutopopulateConfig,
      answers,
      false
    );

    this.differentialDiagnosisScreeningFormGroup.markAsDirty();
  }

  private updateFlags() {
    this.urgentFlag = this.groupHasFlag('urgent');
    this.painFlag = this.groupHasFlag('pain');
    this.systemicFlag = this.groupHasFlag('systemic');
    this.lateralityFlag = this.groupHasFlag('laterality');
    this.allergyInfectionFlag = this.groupHasFlag('allergyInfection');
  }

  private groupHasFlag(group: DewsMedicalHistoryField['group']): boolean {
    if (!this.differentialDiagnosisScreeningFormGroup) {
      return false;
    }
    const controls = this.differentialDiagnosisScreeningFormGroup.controls;
    return DEWS_MEDICAL_HISTORY_FIELDS
      .filter(f => f.group === group)
      .some(f => controls[f.key] && controls[f.key].value === f.expected);
  }
}
